// Ops product-change flow modals (DMS-20260702 / issue #1).
//
//   RequestProductChangeModal — User/Admin pause the order + hand it to Ops.
//   AddEditItemModal          — Ops add a new product or edit an existing one.
//   CompleteProductChangeModal— Ops return the order to Dispatch for review.
//
// Scope is pre-dispatch only; the buttons that open these are gated in
// OrderDetail.jsx (User/Admin own the order in review; Ops own it on_hold).

import { useState } from 'react';
import Modal from '../common/Modal';
import { orderApi } from '../../api/services';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

// --- RequestProductChangeModal --------------------------------------------
export function RequestProductChangeModal({ isOpen, onClose, onDone, order }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await orderApi.requestProductChange(order.id, { reasonNote: note.trim() || null });
      toast.success('Sent to Ops for product change');
      setNote('');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to request change');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request product change">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          This pauses the order and hands it to the Ops team, who will add or
          modify products. It comes back to Dispatch for review afterwards.
        </p>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">What did the customer ask for? (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    placeholder="e.g. Add Tadalafil Xtenda 20mg - 50 Pills"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Send to Ops
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- AddEditItemModal ------------------------------------------------------
// mode: 'add' (needs order) | 'edit' (needs item). Strength / free-pills are
// part of the product name, per the uploaded-order convention.
export function AddEditItemModal({ isOpen, onClose, onDone, mode, order, item }) {
  const isEdit = mode === 'edit';
  const [productName, setProductName] = useState(isEdit ? (item?.product_name || '') : '');
  const [indianBrand, setIndianBrand] = useState(isEdit ? (item?.indian_brand || '') : '');
  const [manufacturer, setManufacturer] = useState(isEdit ? (item?.manufacturer || '') : '');
  const [quantity, setQuantity] = useState(isEdit && item?.quantity != null ? String(item.quantity) : '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!productName.trim()) { toast.error('Product name is required'); return; }
    const qtyParsed = quantity === '' ? null : Number(quantity);
    if ((!isEdit || quantity !== '') && (!Number.isInteger(qtyParsed) || qtyParsed <= 0)) {
      toast.error('Quantity must be a positive whole number'); return;
    }
    setSaving(true);
    try {
      const body = {
        productName: productName.trim(),
        indianBrand: indianBrand.trim() || null,
        manufacturer: manufacturer.trim() || null,
        quantity: qtyParsed,
      };
      const res = isEdit
        ? await orderApi.editItem(item.id, body)
        : await orderApi.addItem(order.id, body);
      toast.success(isEdit ? 'Product updated' : 'Product added');
      if (res.data?.revised_file_batches?.length) {
        toast(`This order's vendor file (batch ${res.data.revised_file_batches.join(', ')}) already went out — regenerate and re-send the revised file from the Dispatch batch page.`, { icon: '⚠️', duration: 10000 });
      }
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || (isEdit ? 'Update failed' : 'Add failed'));
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit product' : 'Add product'}>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Product name *</span>
          <input value={productName} onChange={(e) => setProductName(e.target.value)}
                 placeholder="e.g. Tadalafil Xtenda 20mg - 50 Pills"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <span className="block text-xs text-gray-500 mt-1">Include strength and free-pills in the name, as in the uploaded order.</span>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Brand (optional)</span>
          <input value={indianBrand} onChange={(e) => setIndianBrand(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Manufacturer (optional)</span>
          <input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Quantity {isEdit ? '' : '*'}</span>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)}
                 type="number" min="1" step="1"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          {isEdit && (
            <span className="block text-xs text-gray-500 mt-1">Leave unchanged to keep qty {item?.quantity ?? '—'}. Updates the parcel too.</span>
          )}
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? 'Save' : 'Add product'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- CompleteProductChangeModal -------------------------------------------
// Ops return the order. Choose back to a Dispatch User (the processor picks it
// up via their batch queue) or to the Dispatch Admin.
export function CompleteProductChangeModal({ isOpen, onClose, onDone, order }) {
  const [toTeam, setToTeam] = useState('dispatch_user');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await orderApi.completeProductChange(order.id, { toTeam, reasonNote: note.trim() || null });
      toast.success('Returned to Dispatch for review');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to return order');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Return order to Dispatch">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Send the updated order back for review. The order returns to processing
          and must be re-submitted and approved before dispatch.
        </p>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Assign back to</span>
          <select value={toTeam} onChange={(e) => setToTeam(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="dispatch_user">Dispatch User (continues processing)</option>
            <option value="dispatch_admin">Dispatch Admin (review directly)</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    placeholder="What changed"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Return order
          </button>
        </div>
      </div>
    </Modal>
  );
}
